import ExpanderContainer from "../../../components/core/basic/ExpanderContainer";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import { useMediaQuery } from "@reactuses/core";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../dates/DatesContext";
import { useFetch } from "../../../hooks/api";
import useCustomTableFilters from "../../../hooks/useCustomTableFilters";
import { useCallback, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { formatDatamartFilters } from "../../../utils/formatV2DatamartFilters";
import getMetricSpec from "../../merchandising/utils/metrics";
import Table from "../../merchandising/components/tables/BulkActionsTable";
import { AD_TYPES } from "../../../components/core/blocks/AdPlan/constants";

const usePlacementData = (advertisementId, dimensions, filters, reportType, disablePrevious = false) => {
  const { start, end, preStart, preEnd } = useDates();
  const endpoint = `/api/data_report_v2/${reportType}/stats/`;
  const {
    data: current,
    error,
    isLoading: loading,
  } = useFetch(
    [reportType, start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), advertisementId, dimensions, filters],
    endpoint,
    {
      dimensions: dimensions.join(","),
      filters: formatDatamartFilters({
        ...filters,
        date__lte: end.format("YYYY-MM-DD"),
        date__gte: start.format("YYYY-MM-DD"),
      }),
    },
    {
      select: (res) => res.data ?? [],
    },
  );

  const {
    data: previous,
    error: preError,
    isLoading: preLoading,
  } = useFetch(
    [reportType, preStart.format("YYYY-MM-DD"), preEnd.format("YYYY-MM-DD"), advertisementId, dimensions, filters],
    endpoint,
    {
      dimensions: dimensions.join(","),
      filters: formatDatamartFilters({
        ...filters,
        date__lte: preEnd.format("YYYY-MM-DD"),
        date__gte: preStart.format("YYYY-MM-DD"),
      }),
    },
    {
      enable: !disablePrevious,
      select: (res) => res.data ?? [],
    },
  );

  const data = useMemo(() => {
    if (!current || !current.length) {
      return [];
    }

    const select = (item) => dimensions.map((dim) => item[dim]).join("-");

    const previousMap =
      previous?.reduce((acc, item) => {
        const key = select(item);
        acc[key] = item;
        return acc;
      }, {}) ?? {};

    const compareFn = (key, row, prevRow) => {
      return {
        [`${key}_delta`]: row[key] - prevRow[key],
        [`${key}_percent`]: prevRow[key] !== 0 ? (row[key] - prevRow[key]) / prevRow[key] : null,
        [`${key}_prev`]: prevRow[key],
      };
    };

    return current.map((row) => {
      const key = select(row);
      const prevRow = previousMap?.[key];
      if (prevRow) {
        return {
          ...row,
          ...compareFn("impressions", row, prevRow),
          ...compareFn("clicks", row, prevRow),
          ...compareFn("ad_spend", row, prevRow),
          ...compareFn("cost", row, prevRow),
          ...compareFn("ad_sales", row, prevRow),
          ...compareFn("acos", row, prevRow),
          ...compareFn("roas", row, prevRow),
          ...compareFn("click_thru_rate", row, prevRow),
          ...compareFn("cost_per_click", row, prevRow),
          ...compareFn("conv_rate", row, prevRow),
          ...compareFn("conversion", row, prevRow),
        };
      }
      return row;
    });
  }, [dimensions, current, previous]);

  return {
    data,
    error,
    loading,
    preError,
    preLoading,
  };
};

const Expander = ({ advertisementId, placementClassification, reportType }) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { group } = useDates();
  const { data: _data, loading } = usePlacementData(
    advertisementId,
    ["placementClassification", group],
    {
      advertisement_id: advertisementId,
      marketplace: marketPlace,
      placementClassification,
    },
    reportType,
    true,
  );

  const data = useMemo(() => {
    return _data?.map((row) => ({ ...row, start_date: row.date })) ?? [];
  }, [_data]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["impressions", "ad_spend", "ad_sales"],
    "placementExpander",
  );
  const acosOrRoas = useAcosOrRoas();
  const metrics = useMemo(
    () => [
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("clicks"),
      getMetricSpec("impressions"),
      getMetricSpec("ctr"),
      getMetricSpec("conversion_rate"),
      getMetricSpec("cpc"),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  return (
    <ExpanderContainer>
      <MultiMetricChart
        handleLegendChange={handleLegendChange}
        loading={loading}
        metrics={metrics}
        selectedMetrics={selectedMetrics}
        data={data}
        group={group}
        showAnnotations={false}
      />
    </ExpanderContainer>
  );
};

const ReportTypeSelector = ({ reportType, setReportType, options }) => (
  <FormGroupTile
    type="select"
    multi={false}
    stateName={"reportType"}
    handleChildFormElement={(key, value) => {
      setReportType(value.value);
    }}
    defaultValue={options.find((option) => option.value === reportType)}
    options={options}
  />
);

const PlacementTable = ({ advertisementId, adType }) => {
  const { t } = useTranslation(["advertising"]);
  const tableRef = useRef();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const smallViewportWidthTable = useMediaQuery("(max-width:1400px)");
  const getDefaultReportType = useMemo(() => {
    if (
      adType === AD_TYPES.SPONSORED_BRAND.value ||
      adType === AD_TYPES.SPONSORED_BRAND_V2.value ||
      adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value
    ) {
      return "sbcampaignplacement";
    }
    if (adType === AD_TYPES.SPONSORED_PRODUCT.value || adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value) {
      return "campaignplacement";
    }
    return "campaignplacement";
  }, [adType]);
  const [reportType, setReportType] = useState(getDefaultReportType);
  const { data, loading } = usePlacementData(
    advertisementId,
    ["placementClassification"],
    { advertisement_id: advertisementId, marketplace: marketPlace },
    reportType,
  );

  const columnSpecs = useMemo(() => {
    const perfCol = acosOrRoas({ key: "acos", compareKey: "acos_delta" }, { key: "roas", compareKey: "roas_delta" });
    const cols = [
      { key: "impressions", compareKey: "impressions_percent" },
      { key: "clicks", compareKey: "clicks_percent" },
      { key: "orders", compareKey: "conversion_percent" },
      { key: "ad_spend", compareKey: "ad_spend_percent" },
      { key: "ad_sales", compareKey: "ad_sales_percent" },
      perfCol,
      { key: "cpc", compareKey: "cpc_delta" },
      { key: "conversion_rate", compareKey: "conversion_rate_delta" },
      { key: "ctr", compareKey: "ctr_delta" },
    ];
    return cols;
  }, [acosOrRoas]);
  const filterProps = useCustomTableFilters("campaign-placement-table");

  const ExpanderContext = useCallback(
    ({ row }) => {
      return (
        <Expander
          advertisementId={advertisementId}
          placementClassification={row.original.placementClassification}
          reportType={reportType}
        />
      );
    },
    [advertisementId, reportType],
  );

  const reportTypeOptions = useMemo(
    () => [
      { label: t(($) => $.advertising["Amazon"]), value: getDefaultReportType },
      { label: t(($) => $.advertising["Amazon Business"]), value: "campaignsite" },
    ],
    [getDefaultReportType, t],
  );

  return (
    <>
      <Row>
        <Col xs={12}>
          <Row>
            <Col className="text-center" xs={12} lg={{ offset: 4, span: 4 }}>
              <h2 className="fw-bold my-3" style={{ fontSize: "2rem", color: "#403E3D" }}>
                {t(($) => $.advertising["Placement Report"])}
              </h2>
              {smallViewportWidthTable && reportTypeOptions?.length > 1 ?
                <div
                  className="mx-auto mt-5"
                  style={{
                    position: "relative",
                    maxWidth: "250px",
                    transform: "scale(0.8) translateY(-50%)",
                    zIndex: 1000,
                  }}
                >
                  <ReportTypeSelector
                    reportType={reportType}
                    setReportType={setReportType}
                    options={reportTypeOptions}
                  />
                </div>
              : null}
            </Col>
          </Row>
        </Col>
      </Row>

      <Table
        tableRef={tableRef}
        tableId={"campaign-placement-table"}
        isExpandable
        isStatsLoading={loading}
        isSelectable={false}
        selectionKey={"placementClassification"}
        additionalFilters={
          reportTypeOptions?.length > 1 && !smallViewportWidthTable ?
            <div className="searchTermReportCampaignSelector">
              <ReportTypeSelector reportType={reportType} setReportType={setReportType} options={reportTypeOptions} />
            </div>
          : null
        }
        titleCol={{
          id: "placementClassification",
          isStatic: true,
          Header: t(($) => $.advertising["Placement"]),
          accessor: "placementClassification",
          format: "text",
          style: { width: "300px" },
          width: 300,
          Filter: () => null,
        }}
        data={data}
        columnSpecs={columnSpecs}
        getSearchCriteria={(row) => `${row?.placementClassification}`}
        tableRowDescription={t(($) => $.advertising["Placements"])}
        defaultSorted={[{ id: "impressions", desc: true }]}
        isLoading={loading}
        filterWidget={true}
        hideFilterToggleIcon={true}
        hideResetFiltersIcon={false}
        {...filterProps}
        handleBulkChanges={() => {}}
        ExpandedContent={ExpanderContext}
      ></Table>
    </>
  );
};

export default PlacementTable;
